import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Permission,
  ProductsApi,
  type ClaimRequestViewDto,
} from '@up2stay/up2promo-api-client';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { usePermissions } from '@/core/hooks/use-permissions';
import {
  coarseDuration,
  partitionClaimRequests,
  type ClaimRequestSections,
} from '@/core/lib/claim-requests';
import { useDictionary, useLocale } from '@/core/providers/dictionary-provider';
import { claimRequestsQueryKeys } from '@/core/query-keys/claim-requests-query-keys';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import type { RealtimeEvent } from '@/core/types/realtime-events';
import { buildRedirectUrl } from '@/core/utils/redirect-url.utils';

/**
 * Re-renders every `intervalMs` with the current time (countdowns). Resyncs
 * right away when it becomes enabled, so a paused clock is never shown.
 */
export function useNow(intervalMs: number, enabled = true): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const tick = () => setNow(Date.now());
    const resync = setTimeout(tick, 0);
    const timer = setInterval(tick, intervalMs);
    return () => {
      clearTimeout(resync);
      clearInterval(timer);
    };
  }, [intervalMs, enabled]);
  return now;
}

/** Replaces `{name}` placeholders. */
export function fillClaimTemplate(
  template: string,
  values: Record<string, string | number>,
): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) =>
    key in values ? String(values[key]) : match,
  );
}

export function useClaimDict() {
  return useDictionary().claim;
}

/** "vor 18 Min." / "18 min ago" and "23 Std." / "23 h" for claim rows. */
export function useClaimTimeFormat() {
  const t = useClaimDict().pending;
  const locale = useLocale();
  return useMemo(() => {
    const durationTemplate = {
      minutes: t.durationMinutes,
      hours: t.durationHours,
      days: t.durationDays,
    };
    const agoTemplate = {
      minutes: t.agoMinutes,
      hours: t.agoHours,
      days: t.agoDays,
    };
    const intlLocale = locale === 'de' ? 'de' : 'en';
    const dateFormat = new Intl.DateTimeFormat(intlLocale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const dateTimeFormat = new Intl.DateTimeFormat(intlLocale, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
    return {
      duration: (ms: number) => {
        const { unit, count } = coarseDuration(ms);
        return fillClaimTemplate(durationTemplate[unit], { count });
      },
      ago: (iso: string, now: number) => {
        const ms = now - Date.parse(iso);
        if (ms < 60_000) return t.agoNow;
        const { unit, count } = coarseDuration(ms);
        return fillClaimTemplate(agoTemplate[unit], { count });
      },
      date: (iso: string) => dateFormat.format(new Date(iso)),
      dateTime: (iso: string) => dateTimeFormat.format(new Date(iso)),
    };
  }, [t, locale]);
}

export type UseClaimRequestsResult = ClaimRequestSections & {
  requests: ClaimRequestViewDto[] | undefined;
  isLoading: boolean;
};

/** The workspace's claim requests (`products:read`), newest first. */
export function useClaimRequestsQuery() {
  const workspaceId = useCurrentWorkspace()?.id;
  const { can } = usePermissions();
  const canRead = can(Permission.PRODUCTS_READ);
  return useQuery({
    queryKey: claimRequestsQueryKeys.list(workspaceId),
    queryFn: async () => {
      if (!workspaceId) return [];
      const { data } = await ProductsApi.listClaimRequests({
        path: { workspaceId },
      });
      return data;
    },
    enabled: Boolean(workspaceId) && canRead,
    staleTime: 30_000,
  });
}

/**
 * Pending claim requests for the nav badge. Uses the read-only count endpoint
 * (DEV-802): the list GET expires due rows and announces them, which the shell
 * must not trigger on every mount. Kept live by {@link useClaimCountRealtime}.
 */
export function usePendingClaimCount(): number {
  const workspaceId = useCurrentWorkspace()?.id;
  const { can } = usePermissions();
  const canRead = can(Permission.PRODUCTS_READ);
  const { data } = useQuery({
    queryKey: claimRequestsQueryKeys.count(workspaceId),
    queryFn: async () => {
      if (!workspaceId) return 0;
      const { data } = await ProductsApi.countClaimRequests({
        path: { workspaceId },
      });
      return data.pending;
    },
    enabled: Boolean(workspaceId) && canRead,
    staleTime: 30_000,
  });
  return canRead ? (data ?? 0) : 0;
}

/**
 * Shell-level realtime handler (DEV-802): a `claim_request.updated` event
 * refreshes the nav badge count on every tab, not just Products.
 */
export function useClaimCountRealtime(): (event: RealtimeEvent) => void {
  const workspaceId = useCurrentWorkspace()?.id;
  const queryClient = useQueryClient();
  return useCallback(
    (event: RealtimeEvent) => {
      if (event.type !== 'claim_request.updated') return;
      if (!workspaceId || event.workspaceId !== workspaceId) return;
      void queryClient.invalidateQueries({
        queryKey: claimRequestsQueryKeys.count(workspaceId),
        exact: true,
      });
    },
    [queryClient, workspaceId],
  );
}

/**
 * The workspace's claim requests split into the drawer sections. Pending
 * requests are not in the products list yet, so this is the only place they
 * show (DEV-756).
 */
export function useClaimRequests(): UseClaimRequestsResult {
  const query = useClaimRequestsQuery();
  // Sections only move across a minute boundary (expiry, 7-day window).
  const now = useNow(60_000);
  const sections = useMemo(
    () => partitionClaimRequests(query.data ?? [], now),
    [query.data, now],
  );
  return {
    ...sections,
    requests: query.data,
    isLoading: query.isLoading,
  };
}

/** Claim mutations; each refreshes the workspace's request list. */
export function useClaimRequestActions() {
  const workspaceId = useCurrentWorkspace()?.id;
  const queryClient = useQueryClient();

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: claimRequestsQueryKeys.list(workspaceId),
      }),
    [queryClient, workspaceId],
  );

  const requireWorkspace = useCallback(() => {
    if (!workspaceId) throw new Error('No workspace');
    return workspaceId;
  }, [workspaceId]);

  const requestEmail = useMutation({
    mutationFn: async (hash: string) => {
      const { data } = await ProductsApi.claimRequest({
        path: { workspaceId: requireWorkspace() },
        body: {
          hash: hash.trim(),
          claimVerifyUrl: buildRedirectUrl('/claim/verify'),
        },
      });
      return data;
    },
    onSettled: invalidate,
  });

  const requestManual = useMutation({
    mutationFn: async (input: { hash: string; note: string }) => {
      const note = input.note.trim();
      const { data } = await ProductsApi.claimRequestManual({
        path: { workspaceId: requireWorkspace() },
        body: { hash: input.hash.trim(), ...(note ? { note } : {}) },
      });
      return data;
    },
    onSettled: invalidate,
  });

  const resend = useMutation({
    mutationFn: async (requestId: string) => {
      const { data } = await ProductsApi.resendClaimRequest({
        path: { workspaceId: requireWorkspace(), requestId },
        body: { claimVerifyUrl: buildRedirectUrl('/claim/verify') },
      });
      return data;
    },
    onSettled: invalidate,
  });

  const cancel = useMutation({
    mutationFn: async (requestId: string) => {
      const { data } = await ProductsApi.cancelClaimRequest({
        path: { workspaceId: requireWorkspace(), requestId },
      });
      return data;
    },
    onSettled: invalidate,
  });

  return { requestEmail, requestManual, resend, cancel };
}
